import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogClose,
	DialogDescription,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionsPageFilterFormSchema } from '@/features/transactions/validators/TransactionsPageFilterValidator';
import { useTransactionsFilter } from '@/features/transactions/hooks/useTransactionsFilter';
import { TransactionType } from '@/features/transactions/types/transactions-dto';
import EDatePicker from '@/components/everridge/EDatePicker';
import ApartmentsLookup from '@/features/apartments/components/ApartmentsLookup';
import EListPopupPicker from '@/components/everridge/EListPopupPicker';
import EEnumPopupPicker from '@/components/everridge/EEnumPopupPicker';

type FormData = z.infer<typeof TransactionsPageFilterFormSchema>;

type Props = {
	onClose: () => void;
};

const TransactionsFilter: FC<Props> = ({ onClose }) => {
	const [isOpen, setIsOpen] = useState(true);
	const { t } = useTranslation();

	const { filter, setFilter, resetFilter } = useTransactionsFilter();

	const { control, handleSubmit, reset } = useForm<FormData>({
		resolver: zodResolver(TransactionsPageFilterFormSchema),
		values: {
			startDate: filter.startDate,
			endDate: filter.endDate,
			amountRange: filter.amountRange,
			apartmentId: filter.apartmentId,
			account: filter.account,
		},
	});

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleSubmitFilterForm = (form: FormData) => {
		setFilter(form);
		handleClose();
	};

	const handleResetFilter = () => {
		reset({
			startDate: null,
			endDate: null,
			amountRange: null,
			apartmentId: [],
			account: null,
		});
		resetFilter();
	};

	const accountType = Object.values(TransactionType).filter(
		(value) => typeof value === 'string',
	);

	const amountRanges = ['0-100', '100-200', '200-500', '500+'];

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogDescription />
					<DialogTitle>
						{t('elements.components.filter.title')}
						<DialogClose className='ml-auto cursor-pointer'>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<div className='flex gap-4'>
						<EDatePicker
							control={control}
							fieldName={'startDate'}
							label={t('elements.form.startDate.label')}
							placeholder={t(
								'elements.form.startDate.placeholder',
							)}
						/>
						<EDatePicker
							control={control}
							fieldName={'endDate'}
							label={t('elements.form.endDate.label')}
							placeholder={t('elements.form.endDate.placeholder')}
						/>
					</div>
					<ApartmentsLookup
						control={control}
						fieldName={'apartmentId'}
						selectVariant='multi'
						label={t('elements.form.apartments.label')}
						placeholder={t('elements.form.apartments.placeholder')}
					/>
					<EEnumPopupPicker
						control={control}
						fieldName={'account'}
						label={t('elements.form.account.label')}
						placeholder={t('elements.form.account.placeholder')}
						list={accountType}
					/>
					<EListPopupPicker
						control={control}
						fieldName={'amountRange'}
						label={t('elements.form.amount.label')}
						placeholder={t('elements.form.amount.placeholder')}
						list={amountRanges}
					/>
				</div>
				<DialogFooter className={'justify-between'}>
					<Button
						variant='outline'
						onClick={handleResetFilter}>
						{t('elements.buttons.reset')}
					</Button>
					<Button onClick={handleSubmit(handleSubmitFilterForm)}>
						{t('elements.buttons.apply')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TransactionsFilter;
